import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import PageShell from "../components/layout/PageShell";
import CinematicHero from "../components/layout/CinematicHero";
import Container from "../components/primitives/Container";
import Section from "../components/primitives/Section";
import Eyebrow from "../components/primitives/Eyebrow";
import Reveal from "../components/primitives/Reveal";
import NumberedItem from "../components/editorial/NumberedItem";
import ParallaxImage from "../components/editorial/ParallaxImage";
import LivingImage from "../components/editorial/LivingImage";
import SectionAnchor from "../components/editorial/SectionAnchor";
import WorldMap from "../components/editorial/WorldMap";
import SeoHead from "../components/seo/SeoHead";
import { websiteLd } from "../components/seo/structuredData";
import { EASE, prefersReducedMotion } from "../lib/motion";
import { IMAGES } from "../content/images";
import { HOME } from "../content/site";

const API = process.env.REACT_APP_BACKEND_URL;

const SectionDisplay = ({ children, className = "" }) => (
  <h2
    className={`font-serif font-normal text-navy text-balance ${className}`}
    style={{
      fontSize: "clamp(40px, 5.5vw, 80px)",
      lineHeight: 1.05,
      letterSpacing: "-0.01em",
    }}
  >
    {children}
  </h2>
);

/**
 * HomeHeroOverlay — the editorial overlay content for the Home cinematic hero.
 * Retains the "The Art of Capital" eyebrow + "Capital is abundant. / Judgment is rare."
 * typographic block, staggered into view. Consumes CinematicHero for image, motion,
 * parallax, atmospheric drift, scroll cue and bottom fade into the content section.
 */
const HomeHeroOverlay = () => {
  const reduce = prefersReducedMotion();
  const t = {
    eyebrow: reduce ? { duration: 0 } : { duration: 1.0, ease: EASE, delay: 1.4 },
    line1: reduce ? { duration: 0 } : { duration: 1.0, ease: EASE, delay: 1.56 },
    line2: reduce ? { duration: 0 } : { duration: 1.0, ease: EASE, delay: 1.72 },
  };
  return (
    <>
      <motion.div
        className="flex items-center gap-4"
        initial={reduce ? false : { opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={t.eyebrow}
      >
        <span aria-hidden="true" className="block h-px bg-gold" style={{ width: 24 }} />
        <span
          className="font-sans uppercase text-gold"
          style={{ fontSize: "11px", letterSpacing: "0.22em", fontWeight: 500 }}
        >
          The Art of Capital
        </span>
      </motion.div>

      <div className="mt-8">
        <motion.p
          className="font-serif font-normal text-offwhite"
          initial={reduce ? false : { opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={t.line1}
          style={{
            fontSize: "clamp(40px, 7.4vw, 116px)",
            lineHeight: 1.02,
            letterSpacing: "-0.02em",
            textShadow: "0 1px 24px rgba(11,19,43,0.35)",
          }}
        >
          Capital is abundant.
        </motion.p>
        <motion.p
          className="font-serif italic"
          initial={reduce ? false : { opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={t.line2}
          style={{
            color: "#C6AF74",
            fontSize: "clamp(40px, 7.4vw, 116px)",
            lineHeight: 1.02,
            letterSpacing: "-0.02em",
            marginTop: 12,
            fontWeight: 400,
            textShadow: "0 1px 24px rgba(11,19,43,0.35)",
          }}
        >
          Judgment is rare.
        </motion.p>
      </div>
    </>
  );
};

const Home = () => {
  const [letters, setLetters] = useState([]);

  useEffect(() => {
    let abort = false;
    fetch(`${API}/api/insights`)
      .then((r) => r.json())
      .then((data) => {
        if (!abort && Array.isArray(data)) setLetters(data.slice(0, 3));
      })
      .catch(() => {
        /* silent — preview is optional */
      });
    return () => {
      abort = true;
    };
  }, []);

  return (
    <PageShell>
      <SeoHead
        title="The Art of Capital"
        description="A privately held investment institution. Long-term capital across industrials, infrastructure, energy, and manufacturing."
        path="/"
        ogImage={IMAGES.home_hero_promenade}
        structuredData={websiteLd()}
      />

      <CinematicHero
        image={IMAGES.home_hero_promenade}
        imageAlt="Waterfront promenade at blue hour — the institution at the edge of a working harbour."
        eager
        data-testid="home-hero"
      >
        <HomeHeroOverlay />
      </CinematicHero>

      {/* Who We Are */}
      <Section tone="light" className="py-20 md:py-24 lg:py-28" data-testid="home-who-we-are">
        <Container>
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-24">
            <Reveal className="lg:col-span-7">
              <Eyebrow tone="gold">{HOME.whoWeAre.eyebrow}</Eyebrow>
              <SectionDisplay className="mt-8">{HOME.whoWeAre.heading}</SectionDisplay>
            </Reveal>
            <Reveal className="lg:col-span-5 lg:pt-4" delay={120}>
              <span aria-hidden="true" className="block h-px w-12 bg-gold" />
              <p className="mt-8 font-sans text-[17px] sm:text-[18px] leading-[1.75] text-navy/80 max-w-prose">
                {HOME.whoWeAre.paragraph}
              </p>
            </Reveal>
          </div>
        </Container>
      </Section>

      {/* Anchor 1 — Who We Are → Investment Focus */}
      <SectionAnchor
        image={IMAGES.modern_library_night}
        alt=""
        eyebrow="Next — Where We Focus"
        testId="home-anchor-focus"
      />

      {/* Investment Focus */}
      <Section tone="light" className="py-20 md:py-24 lg:py-28" data-testid="home-investment-focus">
        <Container>
          <Reveal className="max-w-2xl">
            <Eyebrow tone="gold">{HOME.investmentFocus.eyebrow}</Eyebrow>
            <SectionDisplay className="mt-8">{HOME.investmentFocus.heading}</SectionDisplay>
          </Reveal>

          <div className="mt-14 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-x-12 gap-y-14">
            {HOME.investmentFocus.items.map((name, i) => {
              const focusImages = [
                IMAGES.home_focus_industrial_battery,
                IMAGES.home_focus_energy_lng_jetty,
                IMAGES.home_focus_infrastructure_airport_roof,
                IMAGES.sector_tower,
                IMAGES.home_focus_special_situations_boardroom,
                IMAGES.home_focus_crossborder_airport_apron,
                IMAGES.letter_private_equity,
                IMAGES.home_focus_family_hq,
              ];
              const num = String(i + 1).padStart(2, "0");
              const focusAlt = [
                "Battery separator production line at blue hour, diagonal roll assembly",
                "LNG loading jetty at blue hour, moored carrier alongside",
                "Airport terminal roof at blue hour, tensile truss architecture",
                "Institutional headquarters facade at blue hour",
                "Empty executive boardroom at blue hour with a single reading lamp",
                "Widebody aircraft at gate at blue hour, cool cyan taxiway lights",
                "Curved architectural stairwell in low light",
                "Heritage industrial headquarters at blue hour with one warm-lit upper-floor office window",
              ];
              return (
                <Reveal key={name} delay={i * 100} variant="image">
                  <div data-testid={`home-focus-card-${i + 1}`} className="group/focus">
                    <div className="aspect-[4/5] w-full max-h-[200px] bg-navy/5 relative">
                      <LivingImage
                        src={focusImages[i]}
                        alt={focusAlt[i]}
                        className="absolute inset-0"
                        variant="editorial"
                        imgStyle={{ filter: "saturate(0.85) brightness(0.95)" }}
                      />
                      <span
                        aria-hidden="true"
                        className="pointer-events-none absolute inset-0 opacity-0 group-hover/focus:opacity-100 transition-opacity duration-500 z-10"
                        style={{ boxShadow: "inset 0 0 0 1px rgba(184,162,106,0.25)" }}
                      />
                    </div>
                    <span
                      aria-hidden="true"
                      className="block h-px bg-gold mt-6"
                      style={{ width: 24 }}
                    />
                    <p
                      className="mt-4 font-sans uppercase text-gold"
                      style={{ fontSize: "11px", letterSpacing: "0.22em", fontWeight: 500 }}
                    >
                      {num}
                    </p>
                    <p
                      className="mt-2 font-serif text-navy"
                      style={{ fontSize: "clamp(20px, 1.8vw, 26px)", lineHeight: 1.2 }}
                    >
                      {name}
                    </p>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </Container>
      </Section>

      {/* Investment Philosophy — dark editorial break */}
      <section
        data-testid="home-philosophy"
        data-section-tone="dark"
        className="bg-[var(--q-bg-dark)] text-offwhite py-20 md:py-24 lg:py-28"
      >
        <Container>
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-16 lg:gap-24 items-center">
            <Reveal className="lg:col-span-6">
              <ParallaxImage
                src={IMAGES.firmBoardroom}
                alt="An empty institutional boardroom in low light"
                className="aspect-[4/5] w-full"
                overlayStyle={{
                  background:
                    "linear-gradient(180deg, rgba(15,19,42,0.15) 0%, rgba(15,19,42,0.55) 100%)",
                }}
              />
            </Reveal>

            <Reveal className="lg:col-span-6" delay={120}>
              <Eyebrow tone="gold">{HOME.philosophy.eyebrow}</Eyebrow>
              <h2
                className="mt-8 font-serif font-normal text-offwhite text-balance"
                style={{
                  fontSize: "clamp(40px, 5.5vw, 72px)",
                  lineHeight: 1.05,
                  letterSpacing: "-0.01em",
                }}
              >
                {HOME.philosophy.heading}
              </h2>

              <ul className="mt-14 space-y-8">
                {HOME.philosophy.lines.map((line, i) => (
                  <li key={i} className="flex flex-col gap-8">
                    <p
                      className="font-serif italic text-offwhite"
                      style={{
                        fontSize: "clamp(24px, 3vw, 36px)",
                        lineHeight: 1.25,
                      }}
                    >
                      {line}
                    </p>
                    {i < HOME.philosophy.lines.length - 1 ? (
                      <span aria-hidden="true" className="block h-px w-8 bg-gold" />
                    ) : null}
                  </li>
                ))}
              </ul>
            </Reveal>
          </div>
        </Container>
      </section>

      {/* Global Presence preview */}
      <Section tone="light" className="py-20 md:py-24 lg:py-28" data-testid="home-global-preview">
        <Container>
          <Reveal className="max-w-3xl">
            <Eyebrow tone="gold">Global Presence</Eyebrow>
            <h2
              className="mt-8 font-serif font-normal text-navy text-balance"
              style={{
                fontSize: "clamp(36px, 5vw, 72px)",
                lineHeight: 1.05,
                letterSpacing: "-0.01em",
              }}
            >
              From the United States, with strategic presence in Luxembourg and Dubai.
            </h2>
          </Reveal>

          <Reveal className="mt-10" delay={120}>
            <div className="max-w-5xl mx-auto" style={{ maxHeight: 460 }}>
              <WorldMap preview />
            </div>
          </Reveal>

          <Reveal className="mt-10" delay={200}>
            <Link
              to="/global-presence"
              data-testid="home-global-link"
              className="group inline-flex items-center gap-3 font-sans uppercase text-navy/70 hover:text-navy transition-colors"
              style={{ fontSize: "11px", letterSpacing: "0.22em" }}
            >
              <span className="relative">
                View global presence
                <span
                  aria-hidden="true"
                  className="absolute left-0 right-0 -bottom-1 h-px bg-gold scale-x-0 origin-left group-hover:scale-x-100 transition-transform duration-300 ease-out"
                />
              </span>
              <span aria-hidden="true" className="transition-transform duration-300 group-hover:translate-x-1">→</span>
            </Link>
          </Reveal>
        </Container>
      </Section>

      {/* Investor Portal — discreet invitation-only channel; anchor before Letters */}
      <section data-testid="home-investor-portal" className="bg-navy py-32 md:py-40">
        <Container>
          <Reveal>
            <a
              href="/investor-portal"
              data-testid="home-investor-portal-card"
              className="group relative block overflow-hidden bg-navy border-t border-gold/50 transition-transform duration-500 ease-out hover:scale-[1.02] focus:scale-[1.02] focus:outline-none"
              style={{ padding: "48px clamp(24px, 4vw, 64px) 56px" }}
            >
              {/* aged-brass diagonal glass sweep — 18s traversal, 8% opacity */}
              <span
                aria-hidden="true"
                className="pointer-events-none absolute inset-0"
                style={{
                  background:
                    "linear-gradient(115deg, transparent 40%, rgba(194,161,90,0.08) 50%, transparent 60%)",
                  backgroundSize: "300% 300%",
                  animation: "quantumSweep 18s linear infinite",
                }}
              />
              {/* hover inner border */}
              <span
                aria-hidden="true"
                className="pointer-events-none absolute inset-0 border border-gold/0 group-hover:border-gold/40 group-focus:border-gold/40 transition-colors duration-500"
              />
              <div className="relative grid grid-cols-1 lg:grid-cols-12 gap-10 items-baseline">
                <div className="lg:col-span-7">
                  <p
                    className="font-sans uppercase text-gold"
                    style={{ fontSize: 11, letterSpacing: "0.22em", fontWeight: 500 }}
                  >
                    Investor Portal
                  </p>
                  <h3
                    className="mt-6 font-serif font-normal text-offwhite text-balance"
                    style={{ fontSize: "clamp(30px, 3.6vw, 48px)", lineHeight: 1.15, letterSpacing: "-0.01em" }}
                  >
                    Access by Invitation.
                  </h3>
                  <p className="mt-6 font-sans text-[15px] sm:text-[16px] leading-[1.75] text-offwhite/70 max-w-[60ch]">
                    A private access channel for existing partners and institutional counterparties.
                  </p>
                </div>
                <div className="lg:col-span-5 lg:text-right">
                  <span
                    className="inline-flex items-baseline gap-3 font-serif text-offwhite"
                    style={{ fontSize: "clamp(18px, 1.8vw, 22px)" }}
                  >
                    <span className="relative">
                      Request Access
                      <span
                        aria-hidden="true"
                        className="absolute left-0 right-0 -bottom-1 h-px bg-gold scale-x-0 origin-left transition-transform duration-500 ease-out group-hover:scale-x-100 group-focus:scale-x-100"
                      />
                    </span>
                    <span aria-hidden="true" className="text-gold text-lg leading-none transition-transform duration-500 ease-out group-hover:translate-x-1">&rarr;</span>
                  </span>
                </div>
              </div>
            </a>
          </Reveal>
        </Container>
      </section>

      {/* Quantum Letters preview — image first, then heading, then list, then CTA */}
      <Section tone="light" className="py-20 md:py-24 lg:py-28" data-testid="home-letters-preview">
        <Reveal variant="image">
          <div
            className="bg-navy/5"
            style={{
              height: "clamp(280px, 33.75vw, 480px)",
              width: "100vw",
              marginLeft: "calc(50% - 50vw)",
            }}
          >
            <LivingImage
              src={IMAGES.letters_hero_desk}
              alt="Editorial still life — the Quantum Letters."
              className="w-full h-full"
              variant="editorial"
              data-testid="home-letters-preview-image"
            />
          </div>
        </Reveal>

        <Container>
          <Reveal className="max-w-3xl mt-12">
            <Eyebrow tone="gold">Quantum Letters</Eyebrow>
            <h2
              className="mt-8 font-serif font-normal text-navy text-balance"
              style={{
                fontSize: "clamp(28px, 3vw, 48px)",
                lineHeight: 1.1,
                letterSpacing: "-0.005em",
              }}
            >
              Reflections on Capital, Judgment and the Long Term.
            </h2>
          </Reveal>

          <div className="mt-10 max-w-5xl">
            <ul className="divide-y divide-navy/8 border-t border-navy/8">
              {letters.map((l, i) => (
                <li key={l.slug}>
                  <Reveal delay={i * 80}>
                    <Link
                      to={`/letters/${l.slug}`}
                      data-testid={`home-letter-${l.slug}`}
                      className="group grid grid-cols-1 md:grid-cols-12 gap-6 md:gap-10 py-12 md:py-16 transition-colors"
                    >
                      <div className="md:col-span-3">
                        <span
                          className="font-sans uppercase text-gold"
                          style={{ fontSize: "11px", letterSpacing: "0.22em" }}
                        >
                          Letter No. {l.number}
                        </span>
                        <p
                          className="mt-3 font-sans uppercase text-navy/55"
                          style={{ fontSize: "11px", letterSpacing: "0.18em" }}
                        >
                          {l.season || l.dateline}
                        </p>
                      </div>
                      <div className="md:col-span-9">
                        <h3
                          className="font-serif font-normal text-navy group-hover:text-gold transition-colors duration-300 text-balance"
                          style={{
                            fontSize: "clamp(24px, 2.5vw, 36px)",
                            lineHeight: 1.15,
                            letterSpacing: "-0.005em",
                          }}
                        >
                          {l.title}
                        </h3>
                        <p className="mt-4 font-sans text-[15px] leading-[1.7] text-navy/70 max-w-[58ch]">
                          {l.excerpt}
                        </p>
                        <span
                          className="mt-6 inline-flex items-center gap-3 font-sans uppercase text-navy/85 group-hover:text-gold transition-colors duration-300"
                          style={{ fontSize: "11px", letterSpacing: "0.22em" }}
                        >
                          <span>Read letter</span>
                          <span
                            aria-hidden="true"
                            className="inline-block transition-transform duration-300 group-hover:translate-x-1"
                          >
                            →
                          </span>
                        </span>
                      </div>
                    </Link>
                  </Reveal>
                </li>
              ))}
            </ul>
          </div>

          <Reveal className="mt-14" delay={200}>
            <Link
              to="/letters"
              data-testid="home-letters-link"
              className="group inline-flex items-center gap-3 font-sans uppercase text-navy/70 hover:text-navy transition-colors"
              style={{ fontSize: "11px", letterSpacing: "0.22em" }}
            >
              <span className="relative">
                View all letters
                <span
                  aria-hidden="true"
                  className="absolute left-0 right-0 -bottom-1 h-px bg-gold scale-x-0 origin-left group-hover:scale-x-100 transition-transform duration-300 ease-out"
                />
              </span>
              <span aria-hidden="true" className="transition-transform duration-300 group-hover:translate-x-1">→</span>
            </Link>
          </Reveal>
        </Container>
      </Section>
    </PageShell>
  );
};

export default Home;
